import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { type User } from '@prisma/client'
import {
	json,
	type LoaderFunctionArgs,
	type MetaFunction,
} from '@remix-run/node'
import {
	Link,
	redirect,
	useFetcher,
	useLoaderData,
	useNavigate,
} from '@remix-run/react'
import { CoinsIcon, GiftIcon, InfoIcon, TreeDeciduous } from 'lucide-react'
import posthog from 'posthog-js'
import { useEffect, useState } from 'react'
import { Button } from '#app/components/ui/button'
import { Input } from '#app/components/ui/input'
import { Label } from '#app/components/ui/label'
import { Textarea } from '#app/components/ui/textarea'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '#app/components/ui/tooltip'
import { getUserById } from '#app/models/user/get-user'
import { Interval, PlanId } from '#app/services/stripe/plans'
import { useStoreContext } from '#app/store'
import { authenticator } from '#app/utils/auth.server'
import { isEarthDay } from '#app/utils/is-earth-day.js'

type LoaderData = {
	user: User | null
}

export const handle: SEOHandle = {
	getSitemapEntries: () => [{ route: '/gift-basket' }],
}

export async function loader({ request }: LoaderFunctionArgs) {
	const session = await authenticator.isAuthenticated(request)

	if (!session) return json<LoaderData>({ user: null })

	const user = await getUserById(session.id)

	if (user && !user.name) {
		return redirect('/profile/edit?onboarding=true&redirectTo=/basket')
	}

	return json<LoaderData>({ user })
}

export const meta: MetaFunction<typeof loader> = ({ data }) => [
	{ title: `Your gift basket | Erda` },
	{
		name: 'description',
		content: `Your selection of Plots to gift. Each gifted Plot helps plant two trees in developing communities, combating poverty and environmental harm.`,
	},
	{
		name: 'keywords',
		content: 'gift basket',
	},
	{ name: 'og:title', content: `Your gift basket | Erda` },
	{ name: 'og:type', content: 'website' },
	{ name: 'og:url', content: 'https://erda.app' },
	{
		name: 'og:image',
		content: `https://erda.app/image/gift-basket.png`,
	},
	{ name: 'og:card', content: 'summary_large_image' },
	{ name: 'og:creator', content: '@erda_app' },
	{ name: 'og:site', content: 'https://erda.app' },
	{
		name: 'og:description',
		content: `Your selection of Plots to gift. Each gifted Plot helps plant two trees in developing communities, combating poverty and environmental harm.`,
	},
	{
		name: 'twitter:image',
		content: `https://erda.app/image/gift-basket.png`,
	},
	{ name: 'twitter:card', content: 'summary_large_image' },
	{ name: 'twitter:creator', content: '@erda_app' },
	{ name: 'twitter:title', content: `Your gift basket | Erda` },
	{
		name: 'twitter:description',
		content: `Your selection of Plots to gift. Each gifted Plot helps plant two trees in developing communities, combating poverty and environmental harm.`,
	},
]

export default function Basket() {
	const loaderData = useLoaderData() as LoaderData

	const selectedPlotIds = useStoreContext(s => s.selectedPlotIds)
	const setHighlightedPlotIds = useStoreContext(s => s.setHighlightedPlotIds)
	const setHighlightedPlotGroupIds = useStoreContext(
		s => s.setHighlightedPlotGroupIds,
	)

	const navigate = useNavigate()

	const fetcher = useFetcher()
	const isLoading = fetcher.state !== 'idle'

	const planId = PlanId.PLOT_GIFT
	const planInterval = Interval.YEAR

	const costPerPlot = 2
	const treesPerPlot = 2

	const totalCost = (selectedPlotIds.length * costPerPlot).toFixed(2)
	const plotText = `${selectedPlotIds.length > 1 ? 'these Plots' : 'this Plot'}`

	useEffect(() => {
		if (selectedPlotIds.length === 0) {
			setHighlightedPlotIds([])
			setHighlightedPlotGroupIds([])
			navigate('/')
		}
	}, [
		navigate,
		selectedPlotIds,
		setHighlightedPlotGroupIds,
		setHighlightedPlotIds,
	])

	const [recipientEmail, setRecipientEmail] = useState('')
	const [recipientName, setRecipientName] = useState('')
	const [message, setMessage] = useState('')

	// TODO (med) replace with a more robust email validation
	const isValidEmail = recipientEmail.match(
		/^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/,
	)

	return (
		<div className="flex min-h-full flex-col justify-between">
			<div className="flex flex-col gap-3 pb-4">
				{/* <h2 className="flex gap-1 font-bold">
					<GiftIcon className="h-6 w-6" />
					Gifting Plots
				</h2> */}
				<h1 className="sr-only">Gift basket</h1>
				<p>Gift {plotText} to someone</p>

				<div className="flex w-full flex-1 flex-col justify-between gap-6 py-4">
					<div className="grid w-full items-center gap-1.5">
						<div className="flex justify-between">
							<Label htmlFor="email">Recipient's email</Label>
						</div>
						<Input
							data-vaul-no-drag
							placeholder="example@email.com"
							value={recipientEmail}
							type="email"
							id="email"
							onChange={e => setRecipientEmail(e.target.value)}
						/>
					</div>

					<div className="grid w-full items-center gap-1.5">
						<div className="flex justify-between">
							<Label htmlFor="name">Recipient's name</Label>
							<span className="text-xs text-muted-foreground">
								{recipientName.length} / 50
							</span>
						</div>
						<Input
							data-vaul-no-drag
							placeholder="Chris"
							maxLength={50}
							value={recipientName}
							type="email"
							id="name"
							onChange={e => setRecipientName(e.target.value)}
						/>
					</div>

					<div className="grid w-full items-center gap-1.5">
						<div className="flex justify-between">
							<Label htmlFor="message">Message</Label>
							<span className="text-xs text-muted-foreground">
								{message.length ?? 0} / 160
							</span>
						</div>
						<Textarea
							data-vaul-no-drag
							placeholder="Remember this spot?"
							maxLength={160}
							id="message"
							value={message}
							onChange={e => setMessage(e.target.value)}
						/>
					</div>
				</div>
				<p className="text-sm font-semibold"></p>

				<div className="relative flex gap-2 rounded-lg bg-muted p-3">
					<InfoIcon className="mt-0.5 h-4 w-4 flex-shrink-0" />
					<div className="space-y-2">
						<p className="text-sm">
							After checking out, you'll have chance to customise {plotText} and
							choose when to send the gift email.
						</p>
					</div>
				</div>
			</div>

			<div className="sticky -bottom-10 flex w-full flex-col gap-2 border-t bg-background pb-10 pt-3">
				<p className="flex items-center justify-center gap-2 text-center text-sm">
					<CoinsIcon /> ${totalCost}, renews yearly
				</p>
				<TooltipProvider>
					<Tooltip>
						<TooltipTrigger>
							<div className="-mt-1 flex items-center justify-center gap-2 text-center text-sm">
								<TreeDeciduous />{' '}
								<span>
									Plants{' '}
									{isEarthDay() ? (
										<>
											<span className="line-through">
												{selectedPlotIds.length * treesPerPlot}
											</span>{' '}
											{selectedPlotIds.length * 3}
										</>
									) : (
										selectedPlotIds.length * treesPerPlot
									)}{' '}
									trees, and {selectedPlotIds.length * treesPerPlot} trees on
									renewal
								</span>
								<span className="flex items-center gap-1">
									<InfoIcon className="h-3 w-3" />
									<span className="text-xs">How?</span>
								</span>
							</div>
						</TooltipTrigger>
						<TooltipContent>
							With each gift and renewal, we donate to Trees for the Future (
							<a
								href="https://trees.org"
								target="_blank"
								rel="noreferrer"
								className="inline-flex items-center gap-1 underline"
							>
								https://trees.org
							</a>
							) to plant two trees in developing communities. This donation
							helps break the cycle of hunger, poverty, and environmental
							destruction.
						</TooltipContent>{' '}
					</Tooltip>
				</TooltipProvider>

				{loaderData.user ? (
					// TODO (med) check if the plots are still available (somone else may have bought them in the meantime)
					<fetcher.Form
						action="/resources/stripe/create-checkout"
						method="post"
						className="w-full"
					>
						<div className="flex flex-col gap-2">
							<Button
								data-vaul-no-drag
								name="plan"
								value={JSON.stringify({
									planId,
									planInterval,
									selectedPlots: selectedPlotIds,
									giftMessage: message,
									giftRecipientEmail: recipientEmail,
									giftRecipientName: recipientName,
								})}
								type="submit"
								disabled={!isValidEmail}
								className="relative w-full"
								onClick={() => {
									posthog.capture('gift_checkout_initiated', {
										selectedPlots: selectedPlotIds,
									})
								}}
							>
								<GiftIcon className="h-5 w-5" />
								<span>{isLoading ? 'Loading...' : `Gift ${plotText}`}</span>
							</Button>
						</div>
						<div className="mt-2 flex justify-center">
							<p className="text-xs text-muted-foreground">
								By continuing, you agree to our{' '}
								<Button variant="link" asChild className="text-xs">
									<Link to="/terms?from=/gift-basket">Terms</Link>
								</Button>{' '}
								and{' '}
								<Button variant="link" asChild className="text-xs">
									<Link to="/privacy?from=/gift-basket">Privacy Policy</Link>
								</Button>
								.
							</p>
						</div>
					</fetcher.Form>
				) : (
					<div className="flex flex-col gap-2">
						<Button
							asChild
							data-vaul-no-drag
							className="w-full"
							onClick={() => {
								posthog.capture('gift_redirect_login', {
									selectedPlots: selectedPlotIds,
								})
							}}
						>
							<Link to="/login?redirectTo=/gift-basket">
								<GiftIcon className="h-5 w-5" />
								<span>{`Gift ${plotText}`}</span>
							</Link>
						</Button>
					</div>
				)}
			</div>
		</div>
	)
}
